import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Navigation, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import SocialIcons from "@/components/SocialIcons";

const services = [
  "ديكورات وتصميم داخلي",
  "ترميم مباني",
  "دهانات وورق جدران",
  "مقاولات عامة",
];

const MAPS_URL = "https://goo.gl/maps/qiE3pbAQndbx54w1A";

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", phone: "", service: "" });

  const buildWhatsAppUrl = (number: string) => {
    const text = encodeURIComponent(
      `مرحباً، أنا ${formData.name}\nرقم الجوال: ${formData.phone}\nالخدمة المطلوبة: ${formData.service}`
    );
    return `https://wa.me/${number}?text=${text}`;
  };

  const handleSubmit = (e: React.FormEvent, number: string) => {
    e.preventDefault();
    window.open(buildWhatsAppUrl(number), "_blank");
  };

  return (
    <section id="contact" className="section-padding charcoal-gradient">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="w-12 h-0.5 gold-gradient mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-bold text-marble mb-4">تواصل معنا</h2>
          <p className="text-marble/70 max-w-lg mx-auto">
            نسعد بتواصلكم معنا لمناقشة مشروعكم القادم
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Form */}
          <motion.form
            onSubmit={(e) => handleSubmit(e, "966556641963")}
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-5"
          >
            <input
              type="text"
              placeholder="الاسم الكامل"
              required
              maxLength={100}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full bg-charcoal-light/50 border border-marble/10 rounded-lg px-6 py-4 text-marble placeholder:text-marble/40 focus:outline-none focus:border-primary transition-colors"
            />
            <input
              type="tel"
              placeholder="رقم الجوال"
              required
              maxLength={20}
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full bg-charcoal-light/50 border border-marble/10 rounded-lg px-6 py-4 text-marble placeholder:text-marble/40 focus:outline-none focus:border-primary transition-colors"
            />
            <select
              required
              value={formData.service}
              onChange={(e) => setFormData({ ...formData, service: e.target.value })}
              className="w-full bg-charcoal-light/50 border border-marble/10 rounded-lg px-6 py-4 text-marble focus:outline-none focus:border-primary transition-colors appearance-none"
            >
              <option value="" disabled className="text-charcoal">اختر الخدمة</option>
              {services.map((s) => (
                <option key={s} value={s} className="text-charcoal">{s}</option>
              ))}
            </select>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="submit"
                size="lg"
                className="flex-1 bg-primary text-primary-foreground hover:bg-gold-dark font-semibold py-6 text-lg gold-shadow gap-2"
              >
                <Send className="w-5 h-5" />
                إرسال عبر واتساب ١
              </Button>
              <Button
                type="button"
                size="lg"
                onClick={() => {
                  const form = document.querySelector('form');
                  if (form?.checkValidity()) {
                    window.open(buildWhatsAppUrl("966534859731"), "_blank");
                  } else {
                    form?.reportValidity();
                  }
                }}
                className="flex-1 bg-primary text-primary-foreground hover:bg-gold-dark font-semibold py-6 text-lg gold-shadow gap-2"
              >
                <Send className="w-5 h-5" />
                إرسال عبر واتساب ٢
              </Button>
            </div>
          </motion.form>

          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            {/* Two phone numbers */}
            <div className="space-y-3">
              {[
                { number: "0556641963", intl: "+966 556 641 963" },
                { number: "0534859731", intl: "+966 534 859 731" },
              ].map((p) => (
                <a
                  key={p.number}
                  href={`tel:${p.number}`}
                  className="flex items-center gap-4 group"
                >
                  <div className="w-12 h-12 rounded-lg gold-gradient flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Phone className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div>
                    <div className="text-sm text-marble/60 mb-1">اتصل الآن</div>
                    <div className="text-marble font-medium text-sm sm:text-base" dir="ltr">{p.intl}</div>
                  </div>
                </a>
              ))}
            </div>

            {[
              { icon: Mail, label: "البريد الإلكتروني", value: "hassanabdallahdicoration@gmail.com", href: "mailto:hassanabdallahdicoration@gmail.com" },
              { icon: MapPin, label: "الموقع", value: "الرياض، المملكة العربية السعودية", href: MAPS_URL },
              { icon: Clock, label: "ساعات العمل", value: "نعمل على مدار الساعة 24/7", href: "#" },
            ].map((item) => (
              <a
                key={item.label}
                href={item.href}
                target={item.href.startsWith("http") ? "_blank" : undefined}
                rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                className="flex items-start gap-4 group"
              >
                <div className="w-12 h-12 rounded-lg gold-gradient flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <item.icon className="w-5 h-5 text-primary-foreground" />
                </div>
                <div>
                  <div className="text-sm text-marble/60 mb-1">{item.label}</div>
                  <div className="text-marble font-medium text-sm sm:text-base break-all" dir={item.icon === Mail ? "ltr" : "rtl"}>{item.value}</div>
                </div>
              </a>
            ))}

            {/* Social Media */}
            <div className="pt-4 border-t border-marble/10">
              <div className="text-sm text-marble/60 mb-3">تابعنا على</div>
              <SocialIcons size="lg" />
            </div>
          </motion.div>
        </div>

        {/* Google Maps Embed */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16"
        >
          <div className="rounded-xl overflow-hidden border border-marble/10">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3624.674398!2d46.6753!3d24.7136!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjTCsDQyJzQ5LjAiTiA0NsKwNDAnMzEuMSJF!5e0!3m2!1sar!2ssa!4v1700000000000!5m2!1sar!2ssa"
              width="100%"
              height="350"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="موقعنا على الخريطة"
              className="w-full"
            />
          </div>
          <div className="flex justify-center mt-6">
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-gold-dark font-semibold px-8 py-4 rounded-lg text-lg gold-shadow transition-colors"
            >
              <Navigation className="w-5 h-5" />
              احصل على الاتجاهات
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
